<template>
  <b-container fluid>
    <b-row class="mb-2">
      <b-col class="px-sm-3 px-0">
        <h1>Coupons</h1>
      </b-col>
      <b-col class="px-sm-3 px-0 text-right">
        <b-button variant="primary" to="/coupons/create">
          <b-icon icon="plus"></b-icon> Create Coupon
        </b-button>
      </b-col>
    </b-row>

    <!-- Error State -->
    <b-alert
      v-if="error"
      variant="danger"
      show
      dismissible
      @dismissed="error = null"
    >
      {{ error }}
    </b-alert>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-4">
      <b-spinner variant="primary"></b-spinner>
      <p class="mt-2">Loading coupons...</p>
    </div>

    <!-- Coupons Table -->
    <b-row v-if="!loading && coupons.length > 0" class="overflow-auto">
      <b-col class="px-sm-3 px-0">
        <b-table
          hover
          small
          striped
          responsive
          :fields="fields"
          :items="coupons"
        >
          <template #cell(type)="row">
            {{ row.item.type === 'percent' ? 'Percentage' : 'Amount' }}
          </template>
          <template #cell(amount)="row">
            <span v-if="row.item.type === 'percent'">{{ row.item.amount }}%</span>
            <span v-else>{{ (row.item.amount / 100) | currency }}</span>
          </template>
          <template #cell(active)="row">
            <b-badge :variant="row.item.active ? 'success' : 'secondary'">
              {{ row.item.active ? 'Active' : 'Inactive' }}
            </b-badge>
          </template>
          <template #cell(created_at)="row">
            {{ formatDate(row.item.created_at) }}
          </template>
        </b-table>

        <!-- Pagination -->
        <b-pagination
          v-if="pagination.total > pagination.per_page"
          v-model="currentPage"
          :total-rows="pagination.total"
          :per-page="pagination.per_page"
          class="mt-3"
          @change="onPageChange"
        />
      </b-col>
    </b-row>

    <!-- Empty State -->
    <div v-if="!loading && coupons.length === 0" class="text-center py-5">
      <h4 class="text-muted">No Coupons Found</h4>
      <p class="text-muted">Create your first coupon to get started.</p>
      <b-button variant="primary" to="/coupons/create">
        <b-icon icon="plus"></b-icon> Create Coupon
      </b-button>
    </div>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import CouponsAPI from '../../services/CouponsAPI';

export default {
  name: 'Coupons',
  data() {
    return {
      loading: false,
      error: null,
      coupons: [],
      currentPage: 1,
      pagination: {
        total: 0,
        per_page: 25,
      },
      fields: [
        { key: 'id', label: 'ID', sortable: false },
        { key: 'name', label: 'Name', sortable: false },
        { key: 'code', label: 'Code', sortable: false },
        { key: 'type', label: 'Type', sortable: false },
        { key: 'amount', label: 'Discount', sortable: false },
        { key: 'active', label: 'Status', sortable: false },
        { key: 'created_at', label: 'Created', sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters('session', ['canRefundOrders', 'isAdmin']),
    canAccessCoupons() {
      return this.canRefundOrders || this.isAdmin;
    },
  },
  created() {
    if (!this.canAccessCoupons) {
      this.$router.push('/');
      return;
    }
    this.loadCoupons();
  },
  methods: {
    async loadCoupons() {
      this.loading = true;
      this.error = null;

      try {
        const response = await CouponsAPI.getUserCoupons({
          limit: this.pagination.per_page,
          page: this.currentPage,
        });

        const data = response.data || response;
        this.coupons = data.data || data;
        this.pagination = {
          total: data.total || 0,
          per_page: data.per_page || 25,
        };
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to load coupons';
      } finally {
        this.loading = false;
      }
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadCoupons();
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>
